import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, MapPin, Star } from "lucide-react";
import { scrollToHash } from "./Header";

const HERO_IMG = "/brand/nacho-box.png";

const LINES = [
  { text: "THE SNACK", cls: "text-ink" },
  { text: "THAT'S ALWAYS", cls: "text-tang" },
  { text: "A TREAT!", cls: "font-logo text-tomato" },
];

const lineVariants = {
  hidden: { y: "115%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 0.85, delay: 0.15 + i * 0.13, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function Hero() {
  const { scrollY } = useScroll();
  const yImg = useTransform(scrollY, [0, 600], [0, 56]);
  const yBadge = useTransform(scrollY, [0, 600], [0, -44]);

  return (
    <section id="top" data-testid="hero-section" className="dots-bg relative overflow-hidden bg-cream">
      <div className="pointer-events-none absolute -right-24 -top-24 h-96 w-96 rounded-full bg-sunburst/50 blur-3xl" />
      <div className="pointer-events-none absolute -left-32 bottom-0 h-80 w-80 rounded-full bg-tang/20 blur-3xl" />

      <div className="relative mx-auto grid max-w-7xl items-center gap-14 px-4 pb-20 pt-12 sm:px-6 lg:grid-cols-12 lg:gap-8 lg:pb-28 lg:pt-20">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.05 }}
            data-testid="hero-badge"
            className="inline-flex items-center gap-2 rounded-full border-[3px] border-ink bg-white px-4 py-2 text-xs font-black uppercase tracking-[0.14em] shadow-pop"
          >
            <MapPin className="h-3.5 w-3.5 text-tomato" strokeWidth={3} />
            Now booking events in Bakersfield, CA
          </motion.div>

          <h1
            data-testid="hero-title"
            className="font-display mt-7 text-[17vw] font-bold uppercase leading-[0.92] tracking-tight sm:text-7xl lg:text-[5.6rem] xl:text-[6.4rem]"
          >
            {LINES.map((line, i) => (
              <span key={line.text} className="block overflow-hidden pb-1">
                <motion.span
                  className={`block ${line.cls}`}
                  custom={i}
                  variants={lineVariants}
                  initial="hidden"
                  animate="show"
                >
                  {line.text}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.65 }}
            className="mt-6 max-w-xl text-base font-semibold leading-relaxed text-ink/80 sm:text-lg"
          >
            Sy Sy's Snacks rolls up to vendor events, school events and birthday parties with the
            famous <span className="bg-sunburst px-1.5 font-black">Nacho Box that feeds 4-5 people</span>, because every mom deserves an affordable win at the game.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.8 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <button
              type="button"
              data-testid="hero-book-cta"
              onClick={() => scrollToHash("#book")}
              className="group inline-flex items-center gap-2 rounded-full border-[3px] border-ink bg-tang px-8 py-4 text-base font-black uppercase tracking-wide text-white shadow-pop transition-all duration-200 hover:-translate-y-1 hover:bg-tang-dark hover:shadow-popLg"
            >
              Book Your Event
              <ArrowRight className="h-5 w-5 transition-transform duration-200 group-hover:translate-x-1" strokeWidth={3} />
            </button>
            <button
              type="button"
              data-testid="hero-menu-cta"
              onClick={() => scrollToHash("#menu")}
              className="inline-flex items-center gap-2 rounded-full border-[3px] border-ink bg-white px-8 py-4 text-base font-black uppercase tracking-wide text-ink shadow-pop transition-all duration-200 hover:-translate-y-1 hover:bg-sunburst hover:shadow-popLg"
            >
              See the Menu
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1 }}
            className="mt-8 flex flex-wrap gap-2.5"
          >
            {["Youth-run since 2023", "Feeds 4-5 from $15", "The Snack That's Always a Treat!"].map((chip) => (
              <span
                key={chip}
                className="rounded-full border-2 border-ink bg-sunburst/60 px-3.5 py-1.5 text-xs font-extrabold uppercase tracking-wide"
              >
                {chip}
              </span>
            ))}
          </motion.div>
        </div>

        <div className="relative lg:col-span-5">
          <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.9, delay: 0.35 }} className="relative">
            <svg viewBox="0 0 200 200" className="animate-spin-slow absolute -left-10 -top-12 h-36 w-36 text-sunburst sm:-left-14 sm:h-44 sm:w-44" aria-hidden="true">
              <defs>
                <path id="badge-circle" d="M100,100 m-74,0 a74,74 0 1,1 148,0 a74,74 0 1,1 -148,0" />
              </defs>
              <circle cx="100" cy="100" r="92" fill="#FFC629" stroke="#1C1917" strokeWidth="5" />
              <text className="font-display" fontSize="14.5" fontWeight="700" fill="#1C1917" letterSpacing="2">
                <textPath href="#badge-circle">FEEDS 4-5 ★ ONLY $15 ★ FEEDS 4-5 ★ ONLY $15 ★</textPath>
              </text>
              <text x="100" y="112" textAnchor="middle" fontSize="42" fill="#EF3E36" stroke="#1C1917" strokeWidth="1" className="font-logo">$15</text>
            </svg>

            <motion.div style={{ y: yImg }} className="relative">
              <img
                data-testid="hero-image"
                src={HERO_IMG}
                alt="The famous Sy Sy's Nacho Box loaded with nacho cheese, guacamole, jalapeños, olives and sour cream"
                className="w-full rotate-2 rounded-[2rem] border-4 border-ink object-cover shadow-popXl"
                style={{ aspectRatio: "4/5" }}
              />
            </motion.div>

            <motion.div
              style={{ y: yBadge }}
              className="animate-floaty absolute -bottom-6 -left-4 flex items-center gap-3 rounded-2xl border-[3px] border-ink bg-white px-4 py-3 shadow-pop sm:-left-10"
            >
              <span className="flex h-10 w-10 items-center justify-center rounded-full border-2 border-ink bg-tomato text-white">
                <Star className="h-5 w-5 fill-current" strokeWidth={2.5} />
              </span>
              <span className="leading-tight">
                <span className="block text-sm font-black uppercase">Strawberry Lemonade</span>
                <span className="block text-xs font-bold text-tang">Fresh &amp; ice cold for $7</span>
              </span>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
